/**
 * The kinds of tag a code can belong to. A tag is identified by the pair (tag type, code), so
 * the same code may exist once per type. The names mirror the API's TagType table.
 */
export const TagType = { QR: 'QR', Stamped: 'Stamped' } as const

export type TagType = (typeof TagType)[keyof typeof TagType]

const TAG_TYPES: readonly string[] = Object.values(TagType)

export function isTagType(value: unknown): value is TagType {
  return typeof value === 'string' && TAG_TYPES.includes(value)
}

/**
 * Case-insensitive lookup that returns the canonical name, the TS mirror of the API's
 * TryCanonicalize. The router guard uses it to correct the casing of `:tagType`.
 */
export function toTagType(value: string): TagType | undefined {
  const lower = value.toLowerCase()
  return Object.values(TagType).find((tagType) => tagType.toLowerCase() === lower)
}

/**
 * The `:tagType` path segment for the code-keyed routes, generated from TagType so a new type
 * only needs a new entry above.
 */
export const TAG_TYPE_PARAM = `:tagType(${Object.values(TagType).join('|')})`

/**
 * Per-type code validation.
 *
 * QR stays loose (6 letters or digits) because roughly 769 legacy QR codes end in digits; the
 * stricter QR rule lives on the server only. Stamped is the same rule as the API: a 3-letter
 * prefix and a 1 to 12 character sequential ID of letters, digits and hyphens that is not
 * hyphens only.
 */
export const TAG_CODE_RULES: Record<TagType, RegExp> = {
  QR: /^[A-Za-z0-9]{6}$/,
  Stamped: /^[A-Za-z]{3}(?=.*[A-Za-z0-9])[A-Za-z0-9-]{1,12}$/
}
